import cx from 'classnames'
import { useFontFamily } from 'common/Prose/Toolbar/hooks/useFontFamily'
import { useFontWeight } from 'common/Prose/Toolbar/hooks/useFontWeight'
import { camelCase } from 'lodash'
import { computed, reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { FontFamilyType } from 'utils/fontUtils'
import { Button } from '../Buttons/Button'
import Styles from './FontFamilyGroup.module.scss'
import { FontIcon } from './FontIcon'
import { FontFamilyContext, getStillInsideWindowClickSignaler } from './stores/FontFamilyStore'

export const FontFamilyDropdownButton = observer(function FontFamilyDropdownButton({
  fontName,
}: {
  fontName: FontFamilyType
}) {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const fontFamilyStore = useContext(FontFamilyContext)!
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { setFontFamily } = useFontFamily()
  const { setFontWeight } = useFontWeight()

  const visible = computed(() => fontFamilyStore.filteredFontList.includes(fontName)).get()
  const active = computed(() => fontFamilyStore.activeFontFamilies.has(fontName)).get()
  const selected = computed(() => fontName === fontFamilyStore.currentFontFamily).get()

  useEffect(
    () =>
      reaction(
        () => fontFamilyStore.currentFontFamily,
        () => {
          if (fontName === fontFamilyStore.currentFontFamily) {
            fontFamilyStore.setFontWeightAnchor(buttonRef.current)
          }
        },
      ),
    [fontFamilyStore, fontName, buttonRef],
  )

  useEffect(() => {
    const isMine = getStillInsideWindowClickSignaler(buttonRef)
    fontFamilyStore.registerWindowClickSignaler(isMine)
    return () => fontFamilyStore.unregisterWindowClickSignaler(isMine)
  })

  const handleMouseOver = useCallback(() => {
    if (!buttonRef.current) return
    fontFamilyStore.setCurrentFontFamily(fontName)
  }, [buttonRef, fontFamilyStore, fontName])

  const handleClick = useCallback(() => {
    if (!buttonRef.current) return
    setFontFamily(fontName)
    setFontWeight('Regular')
    fontFamilyStore.reset()
  }, [buttonRef, fontFamilyStore, fontName, setFontFamily, setFontWeight])

  if (!visible) return null

  return (
    <Button
      data-active-font-family={active && visible}
      aria-label={fontName}
      active={active}
      selected={selected}
      className={cx(Styles.familyButton)}
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      ref={buttonRef}
      unStyled
    >
      <FontIcon className={`fontFamily-${camelCase(fontName)}Icon`} />
    </Button>
  )
})
